import { useState } from "react";
import axios from "axios";
import { Info, Send } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/site/Reveal";
import { useContent } from "@/context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", event_date: "", guests: "", message: "" };

const inputClass =
  "w-full bg-transparent border-0 border-b-2 border-stone-200 rounded-none px-0 py-3 text-base font-light text-stone-800 placeholder:text-stone-400 focus:border-terracotta-600 focus:outline-none focus:ring-0 transition-colors duration-300";

const labelClass = "block text-xs font-semibold tracking-[0.18em] uppercase text-stone-500 mb-1";

export default function Preventivi() {
  const { preventivi } = useContent();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.guests) {
      toast.error("Seleziona il numero di ospiti (minimo 15).");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/preventivi`, form);
      toast.success("Richiesta inviata. Vi ricontatteremo al più presto.");
      setForm(EMPTY);
    } catch {
      toast.error("Invio non riuscito. Riprova o chiamaci al telefono.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="preventivi" data-testid="preventivi-section" className="py-24 lg:py-32 bg-sand-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-terracotta-600 mb-4 block">
              {preventivi.overline}
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-olive-900 leading-tight">
              {preventivi.heading}
            </h2>
            <p className="mt-8 text-base md:text-lg font-light text-stone-600 leading-relaxed">
              {preventivi.intro}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div
              data-testid="preventivi-disclaimer"
              className="mt-10 border border-stone-200 bg-sand-100 p-6 flex gap-4"
            >
              <Info className="w-5 h-5 text-terracotta-600 shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
              <p className="text-sm font-light text-stone-600 leading-relaxed">
                {preventivi.disclaimer}
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form data-testid="preventivi-form" onSubmit={submit} className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
            <div>
              <label htmlFor="prev-name" className={labelClass}>Nome e cognome *</label>
              <input
                id="prev-name"
                data-testid="preventivo-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Maria Rossi"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="prev-email" className={labelClass}>Email *</label>
              <input
                id="prev-email"
                type="email"
                data-testid="preventivo-email-input"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="maria@esempio.it"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="prev-phone" className={labelClass}>Telefono *</label>
              <input
                id="prev-phone"
                type="tel"
                data-testid="preventivo-phone-input"
                required
                value={form.phone}
                onChange={set("phone")}
                placeholder="+39 333 000 0000"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="prev-date" className={labelClass}>Data dell'evento</label>
              <input
                id="prev-date"
                type="date"
                data-testid="preventivo-date-input"
                value={form.event_date}
                onChange={set("event_date")}
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="prev-guests" className={labelClass}>Numero di ospiti *</label>
              <select
                id="prev-guests"
                data-testid="preventivo-guests-select"
                required
                value={form.guests}
                onChange={set("guests")}
                className={`${inputClass} cursor-pointer`}
              >
                <option value="" disabled>Seleziona (minimo 15 persone)</option>
                <option value="15-20">15 – 20 persone</option>
                <option value="21-30">21 – 30 persone</option>
                <option value="31-50">31 – 50 persone</option>
                <option value="oltre-50">Oltre 50 persone</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="prev-message" className={labelClass}>Messaggio</label>
              <textarea
                id="prev-message"
                data-testid="preventivo-message-input"
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="Raccontateci l'occasione: cerimonia, cena aziendale, esigenze particolari…"
                className={`${inputClass} resize-none`}
              />
            </div>
            <div className="sm:col-span-2 pt-2">
              <button
                type="submit"
                data-testid="preventivo-submit-button"
                disabled={sending}
                aria-label="Invia richiesta di preventivo"
                className="inline-flex items-center gap-3 px-10 py-4 bg-olive-900 text-sand-50 text-xs font-semibold tracking-[0.18em] uppercase transition-colors duration-300 hover:bg-olive-800 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
              >
                {sending ? "Invio in corso…" : "Invia richiesta"}
                <Send className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
